import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import Table from '@mui/joy/Table';
import Alert from '@mui/joy/Alert';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import Checkbox from '@mui/joy/Checkbox';
import IconButton from '@mui/joy/IconButton';
import Stack from '@mui/joy/Stack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import { branchesApi } from '../../api/branches.api';
import type { Branch, CreateBranchPayload } from '../../types/branch';
import { formatRecordReference } from '../../utils/formatRecordReference';

const EMPTY_FORM: CreateBranchPayload = {
  displayName: '',
  isEnabled: true,
  isDefault: false,
  address: {
    street: '',
    number: '',
    city: '',
    state: '',
    zipCode: '',
    referenceDescription: '',
  },
};

export const BranchesPanel: React.FC = () => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRef, setEditingRef] = useState<string | null>(null);
  const [form, setForm] = useState<CreateBranchPayload>({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await branchesApi.list();
      setBranches(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar las sucursales');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = (): void => {
    setError(null);
    setEditingRef(null);
    setForm({ ...EMPTY_FORM });
    setModalOpen(true);
  };

  const openEdit = (b: Branch): void => {
    setError(null);
    setEditingRef(b.reference);
    setForm({
      displayName: b.displayName,
      isEnabled: b.isEnabled,
      isDefault: b.isDefault,
      address: b.address
        ? {
            street: b.address.street,
            number: b.address.number,
            city: b.address.city,
            state: b.address.state,
            zipCode: b.address.zipCode,
            referenceDescription: b.address.referenceDescription ?? '',
          }
        : { ...EMPTY_FORM.address! },
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const hasAddr =
        form.address &&
        (form.address.street.trim() !== '' || form.address.city.trim() !== '');
      const payload: CreateBranchPayload = {
        displayName: form.displayName.trim(),
        isEnabled: form.isEnabled,
        isDefault: form.isDefault,
        address: hasAddr ? form.address : undefined,
      };
      if (editingRef) {
        await branchesApi.update(editingRef, payload);
      } else {
        await branchesApi.create(payload);
      }
      setModalOpen(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (reference: string): Promise<void> => {
    if (!window.confirm('¿Eliminar esta sucursal?')) {
      return;
    }
    try {
      await branchesApi.remove(reference);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al eliminar');
    }
  };

  const handleSetDefault = async (reference: string): Promise<void> => {
    try {
      await branchesApi.setDefault(reference);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    }
  };

  if (loading) {
    return (
      <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
        Cargando sucursales…
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <Typography level="title-md">Sucursales</Typography>
        <Button size="sm" startDecorator={<AddIcon />} onClick={openCreate}>
          Nueva sucursal
        </Button>
      </Stack>
      {error && !modalOpen ? <Alert color="danger">{error}</Alert> : null}
      <Sheet variant="outlined" sx={{ overflow: 'auto' }}>
        <Table>
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Dirección</th>
              <th>Estado</th>
              <th style={{ width: 120 }} />
            </tr>
          </thead>
          <tbody>
            {branches.length === 0 ? (
              <tr>
                <td colSpan={4}>
                  <Typography level="body-sm" sx={{ color: 'neutral.500', py: 1 }}>
                    No hay sucursales cargadas.
                  </Typography>
                </td>
              </tr>
            ) : (
              branches.map((b) => (
                <tr key={b.reference}>
                  <td>
                    <Typography fontWeight="md">{b.displayName}</Typography>
                    <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                      #{formatRecordReference(b.reference)}
                    </Typography>
                  </td>
                  <td>
                    {b.address
                      ? `${b.address.street} ${b.address.number}, ${b.address.city}`
                      : '—'}
                  </td>
                  <td>
                    {b.isEnabled ? 'Habilitada' : 'Deshabilitada'}
                    {b.isDefault ? ' · Predeterminada' : ''}
                  </td>
                  <td>
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="neutral"
                      onClick={() => void handleSetDefault(b.reference)}
                      disabled={b.isDefault || !b.isEnabled}
                      title="Marcar predeterminada"
                    >
                      {b.isDefault ? <StarIcon /> : <StarBorderIcon />}
                    </IconButton>
                    <IconButton size="sm" variant="plain" color="primary" onClick={() => openEdit(b)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="danger"
                      onClick={() => void handleRemove(b.reference)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Sheet>

      <Modal open={modalOpen} onClose={() => !saving && setModalOpen(false)}>
        <ModalDialog
          component="form"
          onSubmit={(ev) => void handleSubmit(ev)}
          sx={{ maxWidth: 480, width: '100%' }}
        >
          <ModalClose disabled={saving} />
          <Typography level="h4" sx={{ mb: 2 }}>
            {editingRef ? 'Editar sucursal' : 'Nueva sucursal'}
          </Typography>
          {error ? (
            <Alert color="danger" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}
          <Stack spacing={2}>
            <FormControl required>
              <FormLabel>Nombre</FormLabel>
              <Input
                value={form.displayName}
                onChange={(ev) => setForm((f) => ({ ...f, displayName: ev.target.value }))}
              />
            </FormControl>
            <Typography level="title-sm">Dirección (opcional)</Typography>
            <FormControl>
              <FormLabel>Calle</FormLabel>
              <Input
                value={form.address?.street ?? ''}
                onChange={(ev) =>
                  setForm((f) => ({
                    ...f,
                    address: { ...f.address!, street: ev.target.value },
                  }))
                }
              />
            </FormControl>
            <FormControl>
              <FormLabel>Número</FormLabel>
              <Input
                value={form.address?.number ?? ''}
                onChange={(ev) =>
                  setForm((f) => ({
                    ...f,
                    address: { ...f.address!, number: ev.target.value },
                  }))
                }
              />
            </FormControl>
            <FormControl>
              <FormLabel>Ciudad</FormLabel>
              <Input
                value={form.address?.city ?? ''}
                onChange={(ev) =>
                  setForm((f) => ({
                    ...f,
                    address: { ...f.address!, city: ev.target.value },
                  }))
                }
              />
            </FormControl>
            <FormControl>
              <FormLabel>Provincia</FormLabel>
              <Input
                value={form.address?.state ?? ''}
                onChange={(ev) =>
                  setForm((f) => ({
                    ...f,
                    address: { ...f.address!, state: ev.target.value },
                  }))
                }
              />
            </FormControl>
            <FormControl>
              <FormLabel>Código postal</FormLabel>
              <Input
                value={form.address?.zipCode ?? ''}
                onChange={(ev) =>
                  setForm((f) => ({
                    ...f,
                    address: { ...f.address!, zipCode: ev.target.value },
                  }))
                }
              />
            </FormControl>
            <FormControl>
              <FormLabel>Referencia (piso, depto)</FormLabel>
              <Input
                value={form.address?.referenceDescription ?? ''}
                onChange={(ev) =>
                  setForm((f) => ({
                    ...f,
                    address: { ...f.address!, referenceDescription: ev.target.value },
                  }))
                }
              />
            </FormControl>
            <Checkbox
              label="Habilitada"
              checked={form.isEnabled}
              onChange={(ev) => setForm((f) => ({ ...f, isEnabled: ev.target.checked }))}
            />
            <Checkbox
              label="Sucursal predeterminada"
              checked={form.isDefault}
              onChange={(ev) => setForm((f) => ({ ...f, isDefault: ev.target.checked }))}
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button type="button" variant="plain" disabled={saving} onClick={() => setModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" loading={saving}>
                Guardar
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
};
